import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon, Badge, Button, Drawer, Group, Loader, NumberInput, Paper, Stack,
  Table, Text, Textarea, TextInput, Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconEdit, IconPlus, IconReceipt2, IconTrash } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  useCreatePaymentTerm, useDeletePaymentTerm, usePaymentTerms, useUpdatePaymentTerm,
  type PaymentTermDto,
} from './paymentTermsApi';

export function PaymentTermsPage() {
  const { t, i18n } = useTranslation();
  const { data, isLoading } = usePaymentTerms();
  const create = useCreatePaymentTerm();
  const update = useUpdatePaymentTerm();
  const del = useDeletePaymentTerm();

  const [drawer, setDrawer] = useState(false);
  const [editing, setEditing] = useState<PaymentTermDto | null>(null);
  const [deleting, setDeleting] = useState<PaymentTermDto | null>(null);

  const form = useForm({
    initialValues: { nameAr: '', nameEn: '', netDays: 30, description: '', isActive: true },
    validate: { nameAr: (v) => (v.trim() ? null : t('common.errors.required')) },
  });

  const openCreate = () => {
    setEditing(null);
    form.setValues({ nameAr: '', nameEn: '', netDays: 30, description: '', isActive: true });
    form.resetDirty();
    setDrawer(true);
  };
  const openEdit = (p: PaymentTermDto) => {
    setEditing(p);
    form.setValues({ nameAr: p.nameAr, nameEn: p.nameEn ?? '', netDays: p.netDays, description: p.description ?? '', isActive: p.isActive });
    form.resetDirty();
    setDrawer(true);
  };

  const submit = form.onSubmit(async (v) => {
    const req = { nameAr: v.nameAr.trim(), nameEn: v.nameEn.trim() || null, netDays: v.netDays, description: v.description.trim() || null, isActive: v.isActive };
    try {
      if (editing) await update.mutateAsync({ id: editing.paymentTermId, req });
      else await create.mutateAsync(req);
      notifications.show({ color: 'green', title: t('common.toasts.saved'), message: v.nameAr });
      setDrawer(false);
    } catch (err) {
      notifications.show({
        color: 'red', title: t('common.toasts.errorTitle'),
        message: (isAxiosError(err) && (err.response?.data?.detail as string)) || t('common.toasts.errorBody'),
      });
    }
  });

  const rows = data ?? [];

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.accounting')}
        title={t('accounting.paymentTerms.title')}
        subtitle={t('accounting.paymentTerms.subtitle')}
        actions={<Button leftSection={<IconPlus size={16} stroke={1.8} />} onClick={openCreate}>{t('accounting.paymentTerms.create')}</Button>}
      />
      <Paper p={rows.length ? 0 : 'lg'}>
        {isLoading ? (
          <Stack align="center" py={48}><Loader size="sm" /></Stack>
        ) : rows.length === 0 ? (
          <EmptyState icon={<IconReceipt2 size={22} stroke={1.6} />} title={t('accounting.paymentTerms.empty')}
            description={t('accounting.paymentTerms.emptyBody')} actionLabel={t('accounting.paymentTerms.create')} onAction={openCreate} />
        ) : (
          <Table verticalSpacing="md" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('accounting.paymentTerms.name')}</Table.Th>
                <Table.Th>{t('accounting.paymentTerms.netDays')}</Table.Th>
                <Table.Th>{t('accounting.paymentTerms.status')}</Table.Th>
                <Table.Th style={{ width: 100, textAlign: 'end' }}>{t('common.actions')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((p) => (
                <Table.Tr key={p.paymentTermId}>
                  <Table.Td>
                    <Text fw={500} c="var(--wheels-ink-strong)">{i18n.language === 'ar' ? p.nameAr : (p.nameEn ?? p.nameAr)}</Text>
                    {p.description && <Text size="xs" c="dimmed" lineClamp={1}>{p.description}</Text>}
                  </Table.Td>
                  <Table.Td className="font-numeric">{t('accounting.paymentTerms.days', { count: p.netDays })}</Table.Td>
                  <Table.Td>
                    {p.isActive
                      ? <Badge color="green" variant="light" radius="sm">{t('common.active')}</Badge>
                      : <Badge color="gray" variant="light" radius="sm">{t('common.inactive')}</Badge>}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Tooltip label={t('common.edit')} withinPortal>
                        <ActionIcon variant="subtle" color="gray" onClick={() => openEdit(p)} aria-label={t('common.edit')}>
                          <IconEdit size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t('common.delete')} withinPortal>
                        <ActionIcon variant="subtle" color="red" onClick={() => setDeleting(p)} aria-label={t('common.delete')}>
                          <IconTrash size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <Drawer opened={drawer} onClose={() => setDrawer(false)} position={i18n.language === 'ar' ? 'left' : 'right'}
        size="md" padding="lg" title={editing ? t('accounting.paymentTerms.editTitle') : t('accounting.paymentTerms.create')}>
        <form onSubmit={submit}>
          <Stack gap="md">
            <TextInput label={t('accounting.paymentTerms.nameAr')} withAsterisk {...form.getInputProps('nameAr')} />
            <TextInput label={t('accounting.paymentTerms.nameEn')} {...form.getInputProps('nameEn')} />
            <NumberInput label={t('accounting.paymentTerms.netDays')} min={0} {...form.getInputProps('netDays')} />
            <Textarea label={t('accounting.paymentTerms.description')} autosize minRows={2} {...form.getInputProps('description')} />
            <Group justify="flex-end" gap="sm" mt="sm">
              <Button variant="default" onClick={() => setDrawer(false)}>{t('common.cancel')}</Button>
              <Button type="submit" loading={create.isPending || update.isPending}>{editing ? t('common.save') : t('common.create')}</Button>
            </Group>
          </Stack>
        </form>
      </Drawer>

      <ConfirmDialog opened={deleting !== null} onClose={() => setDeleting(null)}
        onConfirm={async () => { if (!deleting) return; await del.mutateAsync(deleting.paymentTermId); setDeleting(null); }}
        title={t('accounting.paymentTerms.deleteTitle')} message={t('accounting.paymentTerms.deleteBody', { name: deleting?.nameAr ?? '' })}
        loading={del.isPending} />
    </Stack>
  );
}
